/**
 * Shift API Service
 *
 * API service for shift management operations
 */

import type { AxiosResponse } from 'axios'
import { apiClient } from '../client'
import { API_ENDPOINTS } from '@/config/api'
import { handleApiError } from '@/utils/apiErrorHandling'
import { authService } from './authService'
import type {
  ShiftListResponse,
  ShiftListParams,
  ShiftItem,
  ShiftTrail,
  CreateShiftPayload,
  UpdateShiftPayload,
  ShiftDispatchersResponse,
  ShiftDefectsListParams,
  ShiftDefectsListResponse,
  ShiftDefectActionPayload,
  ShiftDefectActionResponse,
  CreateRosterShiftPayload,
  CreateRosterShiftResponse,
  ShiftSwapListParams,
} from '@/types/api/shifts'
import type {
  ShiftSwapListResponse,
  SwapActionPayload,
  SwapActionResponse,
} from '@/types/api/shifts'

type ShiftDetailResponse =
  | ShiftListResponse
  | {
      data?: ShiftItem | { shift?: ShiftItem }
      item?: ShiftItem
      shift?: ShiftItem
    }

const extractShiftItem = (value: unknown): ShiftItem | undefined => {
  if (!value || typeof value !== 'object') return undefined

  if ('shift_id' in value) {
    return value as ShiftItem
  }

  if ('shift' in value) {
    return extractShiftItem((value as { shift?: unknown }).shift)
  }

  return undefined
}

class ShiftService {
  private getPrecinctParam(): { precinct_id?: string } {
    try {
      const precinctId = localStorage.getItem('precinctId')
      if (precinctId) return { precinct_id: precinctId }
      return {}
    } catch (error) {
      console.error('getPrecinctParam error:', error)
      return {}
    }
  }

  /**
   * Get shift by ID
   */
  async getShift(
    shiftId: string,
    shiftTrail?: ShiftTrail
  ): Promise<ShiftItem | undefined> {
    try {
      const response: AxiosResponse<ShiftDetailResponse> = await apiClient.get(
        API_ENDPOINTS.SHIFT_BY_ID(shiftId),
        shiftTrail
          ? {
              params: {
                shift_trail: shiftTrail,
              },
            }
          : undefined
      )
      const data = response.data

      if ('items' in data && Array.isArray(data.items)) {
        return data.items.find(item => item.shift_id === shiftId)
      }

      return (
        extractShiftItem(data) ||
        ('data' in data ? extractShiftItem(data.data) : undefined) ||
        ('item' in data ? extractShiftItem(data.item) : undefined)
      )
    } catch (error) {
      throw handleApiError(error, 'getShift')
    }
  }

  /**
   * List shifts with pagination and filters
   */
  async listShifts(params: ShiftListParams = {}): Promise<ShiftListResponse> {
    try {
      const precinctParam = this.getPrecinctParam()
      const precinctInfo = await authService.getPrecinct()

      // Build query parameters according to API specification
      const queryParams: Record<string, any> = {
        // Pagination
        limit: params.limit || 20,
        lastKey: params.lastKey,

        // Filters
        dispatcher_id: params.dispatcher_id,
        backup_dispatcher: params.backup_dispatcher,
        role: params.role,
        shift_type: params.shift_type,
        roster_id: params.roster_id,
        defaulter: params.defaulter || false,

        // Date range
        from: params.from,
        to: params.to,

        // Sorting
        sortBy: params.sortBy || 'dispatcher_id',
        sortOrder: params.sortOrder || 'asc',

        // Precinct
        precinct_id: params.precinct_id || precinctInfo?.precinct_id,
        ...precinctParam,
      }

      // Remove undefined/null values
      Object.keys(queryParams).forEach(key => {
        if (queryParams[key] === undefined || queryParams[key] === null) {
          delete queryParams[key]
        }
      })

      const response: AxiosResponse<ShiftListResponse> = await apiClient.get(
        API_ENDPOINTS.SHIFTS,
        { params: queryParams }
      )

      return response.data
    } catch (error) {
      throw handleApiError(error, 'listShifts')
    }
  }

  /**
   * Search shifts (if your API supports search)
   */
  async searchShifts(
    query: string,
    filters?: Partial<ShiftListParams>
  ): Promise<ShiftItem[]> {
    try {
      const precinctInfo = await authService.getPrecinct()
      const params: Record<string, any> = {
        q: query,
        precinct_id: precinctInfo?.precinct_id,
        limit: 50,
        ...filters,
      }

      const response: AxiosResponse<ShiftListResponse> = await apiClient.get(
        API_ENDPOINTS.SHIFTS,
        { params }
      )

      return response.data.items || []
    } catch (error) {
      throw handleApiError(error, 'searchShifts')
    }
  }

  /**
   * Create a new shift
   */
  async createShift(payload: CreateShiftPayload): Promise<any> {
    try {
      const response: AxiosResponse = await apiClient.post(
        API_ENDPOINTS.SHIFT_CREATE,
        payload
      )
      return response.data
    } catch (error) {
      throw handleApiError(error, 'createShift')
    }
  }

  async createRosterShift(
    payload: CreateRosterShiftPayload
  ): Promise<CreateRosterShiftResponse> {
    try {
      const response: AxiosResponse<CreateRosterShiftResponse> =
        await apiClient.post(API_ENDPOINTS.SHIFT_CREATE, payload)
      return response.data
    } catch (error) {
      throw handleApiError(error, 'createRosterShift')
    }
  }

  /**
   * Update a shift
   */
  async updateShift(
    shiftId: string,
    payload: UpdateShiftPayload
  ): Promise<any> {
    try {
      const response: AxiosResponse = await apiClient.put(
        API_ENDPOINTS.SHIFT_UPDATE(shiftId),
        payload
      )
      return response.data
    } catch (error) {
      throw handleApiError(error, 'updateShift')
    }
  }

  /**
   * Delete a shift
   */
  async deleteShift(shiftId: string): Promise<any> {
    try {
      const response: AxiosResponse = await apiClient.delete(
        API_ENDPOINTS.SHIFT_DELETE(shiftId)
      )
      return response.data
    } catch (error) {
      throw handleApiError(error, 'deleteShift')
    }
  }

  /**
   * Clock in for a shift
   */

  async clockInShift(_shiftId: string, payload: any): Promise<any> {
    try {
      const response: AxiosResponse = await apiClient.put(
        API_ENDPOINTS.SHIFT_CLOCK_IN,
        payload
      )
      return response.data
    } catch (error) {
      throw handleApiError(error, 'clockInShift')
    }
  }
  // In shiftService.ts, update the clockOutShift method:

  /**
   * Clock out for a shift
   */
  async clockOutShift(payload: {
    shift_id: string
    data: {
      title: string
      total_cases_handled: number
      opened_cases_during_shift: number
      closed_cases_during_shift: number
      pending_cases: number
      major_incident: string
      interventions: Array<{
        case_id: string
        intervention_type: string
        details: string
        time: string
      }>
    }
    active_cases: Array<{
      case_id: string
      disposition: string
      note: string
      status: string
    }>
    final_comment: string
    clock_out_consent: boolean
    message: string
    status: string
  }): Promise<any> {
    try {
      const response: AxiosResponse = await apiClient.put(
        API_ENDPOINTS.SHIFT_CLOCK_OUT,
        payload
      )
      return response.data
    } catch (error) {
      throw handleApiError(error, 'clockOutShift')
    }
  }

  /**
   * Get shift swap requests
   */
  async listShiftSwapRequests(
    params: ShiftSwapListParams = {}
  ): Promise<ShiftSwapListResponse> {
    try {
      const precinctParam = this.getPrecinctParam()
      const precinctInfo = await authService.getPrecinct()

      const queryParams = {
        limit: params.limit || 50,
        lastKey: params.lastKey || null,
        precinct_id: params.precinct_id || precinctInfo?.precinct_id,
        ...precinctParam,
      }

      const response: AxiosResponse<ShiftSwapListResponse> =
        await apiClient.get(API_ENDPOINTS.SHIFT_SWAP_REQUESTS, {
          params: queryParams,
        })
      return response.data
    } catch (error) {
      throw handleApiError(error, 'listShiftSwapRequests')
    }
  }

  /**
   * Get shift dispatchers
   */
  async getShiftDispatchers(shiftId: string): Promise<any> {
    try {
      const response: AxiosResponse<ShiftDispatchersResponse> =
        await apiClient.get(API_ENDPOINTS.SHIFT_DISPATCHERS(shiftId))
      return response.data
    } catch (error) {
      throw handleApiError(error, 'getShiftDispatchers')
    }
  }

  /**
   * Request shift swap
   */
  async requestShiftSwap(payload: any): Promise<any> {
    try {
      const response: AxiosResponse = await apiClient.post(
        API_ENDPOINTS.SHIFT_SWAP_REQUEST,
        payload
      )
      return response.data
    } catch (error) {
      throw handleApiError(error, 'requestShiftSwap')
    }
  }

  /**
   * Process shift swap request (approve/decline)
   */
  async processShiftSwap(
    payload: SwapActionPayload
  ): Promise<SwapActionResponse> {
    try {
      const response: AxiosResponse<SwapActionResponse> = await apiClient.put(
        API_ENDPOINTS.SHIFT_SWAP_ACTION,
        payload
      )
      return response.data
    } catch (error) {
      throw handleApiError(error, 'processShiftSwap')
    }
  }

  /**
   * Get shift defects for precinct
   */
  async listShiftDefects(
    params: ShiftDefectsListParams = {}
  ): Promise<ShiftDefectsListResponse> {
    try {
      const precinctParam = this.getPrecinctParam()
      const precinctInfo = await authService.getPrecinct()

      const queryParams: Record<string, any> = {
        limit: params.limit || 20,
        lastKey: params.lastKey || null,
        precinct_id: params.precinct_id || precinctInfo?.precinct_id,
        ...precinctParam,
      }

      Object.keys(queryParams).forEach(key => {
        if (queryParams[key] === undefined || queryParams[key] === null) {
          delete queryParams[key]
        }
      })

      const response: AxiosResponse<ShiftDefectsListResponse> =
        await apiClient.get(API_ENDPOINTS.SHIFT_DEFECTS, {
          params: queryParams,
        })

      return response.data
    } catch (error) {
      throw handleApiError(error, 'listShiftDefects')
    }
  }

  /**
   * Log supervisor action for a shift defect
   */
  async logShiftDefectAction(
    payload: ShiftDefectActionPayload
  ): Promise<ShiftDefectActionResponse> {
    try {
      const response: AxiosResponse<ShiftDefectActionResponse> =
        await apiClient.put(API_ENDPOINTS.SHIFT_DEFECT_ACTION, payload)
      return response.data
    } catch (error) {
      throw handleApiError(error, 'logShiftDefectAction')
    }
  }
}

export const shiftService = new ShiftService()
export default shiftService
